import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom/cjs/react-router-dom'
import cn from 'classnames'
import { isTabbed } from '../../utils/orderStates'
import { isEmpty } from '../../VNUtil/VNUtils'
import { getNetworkAvailableValue } from '../../utils/networkConnected'
import { ToastManager } from '../../utils/toastManager'
import { bridgePaymentMethods } from '../../reducers/peripheral'
import { APPLICATION_STRINGS } from '../../strings'
import { CFD_POS_PAYMENT_FLOWS } from '../../VNCustomerFacingDisplay/Enums'
import { VNCFD_TRANSACTION_RESULT } from '../../VNCustomerFacingDisplay/ActionTypes'
import { PAYMENT_TYPES } from '../../utils/paymentTypes'
import { MODES } from '../../VNMode/Reducer'

import Button from '../../components/Button'
import PayNowButton from '../../VNTabs/components/PayNowButton'

import useSpinner from '../../context/hooks/useSpinner'
import { setRoute } from '../../VNRoutes/ActionCreators'
import {
  setCardAuthStepOne,
  setKitchenPrintErrorDialogOpen,
  setStartATabModal,
  setTabPaymentMethodModal,
} from '../../VNDialogs/ActionCreators'
import { setIsTabNameRequired } from '../../VNTabs/ActionCreators'
import { setCalculatedCurrentCartValues, setCurrentPaymentFlow } from '../../VNCustomerFacingDisplay/ActionCreators'
import { addItemsToTab, addItemsToTabOffline, startATab } from '../../actions/order'
import { setBridgePaymentMethod } from '../../actions/peripheral'

import {
  getKitchenPrintingEnabled,
  getRequirePaymentForTabs,
  getTabsModeEnabled,
  getUseNormalTaxRounding,
  getUseTaxClasses,
} from '../../selectors/menus'
import { getMutationInProgress, getOrderInProgress } from '../../selectors/order'
import { getCartItems, getTaxByItem, getTaxRate, hasCartItems2 } from '../../selectors/cart'
import { getDeviceMode } from '../../VNMode/Selectors'
import { getCurrentPaymentFlow, getTransactionResult } from '../../VNCustomerFacingDisplay/Selectors'

import styles from './VNCartButtons.module.scss'

import { getCardAuthStepOne, getCardAuthStepTwo } from '../../VNDialogs/Selectors'
import { getIsTabNameRequired } from '../../VNTabs/Selectors'
import { bridge_sendExecuteAuthRequest } from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { getDeviceSerial } from '../../utils/deviceData'
import { getTransactionBrandingConfig } from '../../utils/buildSettings'
import { centsToDollars } from '../../utils/formatters'
import useRemoteOrderTotal from '../../hooks/useRemoteOrderTotal'
import { calculateCartTotals } from '../../utils/orderTotalUtils'
import { getBridgePaymentMethod } from '../../selectors/peripheral'
import { getMenuServiceCharge } from '../../selectors/items'
import { getIsOrderTaxExempt, getTaxClasses, getTaxRules } from '../../VNTaxes/Selectors'

const VNTabButtons = ({ cardReaderData }) => {
  const dispatch = useDispatch()
  const history = useHistory()

  // selectors
  const tabsModeEnabled = useSelector(getTabsModeEnabled)
  const orderInProgress = useSelector(getOrderInProgress)
  const hasCartItems = useSelector(hasCartItems2)
  const mutationInProgress = useSelector(getMutationInProgress)
  const cfdMode = useSelector(getDeviceMode)
  const cfdAuthResponse = useSelector(getTransactionResult)
  const requirePaymentForTabs = useSelector(getRequirePaymentForTabs)
  const cloudPrintingEnabled = useSelector(getKitchenPrintingEnabled)
  const currentPaymentFlow = useSelector(getCurrentPaymentFlow)
  const stepTwoModal = useSelector(getCardAuthStepTwo)
  const stepOneModal = useSelector(getCardAuthStepOne)
  const tabNameRequired = useSelector(getIsTabNameRequired)
  const bridgePaymentMethod = useSelector(getBridgePaymentMethod)
  const taxByItem = useSelector(getTaxByItem)
  const menuServiceCharge = useSelector(getMenuServiceCharge)
  const taxRate = useSelector(getTaxRate)
  const currentCart = useSelector(getCartItems)
  const useNormalTaxRounding = useSelector(getUseNormalTaxRounding)
  const taxClasses = useSelector(getTaxClasses)
  const taxRules = useSelector(getTaxRules)
  const useTaxClasses = useSelector(getUseTaxClasses)
  const taxExempt = useSelector(getIsOrderTaxExempt)

  // state
  const tabNameInputRef = useRef()
  const [showStartTabModal, setShowStartTabModal] = useState(false)
  const [showSelectTabPaymentMethod, setShowSelectTabPaymentMethod] = useState(false)

  // hooks
  const { startSpinner, stopSpinner } = useSpinner()

  const [loadRemoteOrderTotal, { data: remoteOrderTotal, success, failed, loading }] = useRemoteOrderTotal()

  const getInitialInputValue = useMemo(() => {
    if (cfdMode === MODES.POS) return cfdAuthResponse?.get('cardholderName')?.trim()

    return cardReaderData?.cardholderName?.trim() || ''
  }, [cardReaderData?.cardholderName, cfdAuthResponse, cfdMode])

  const processPromotions = useCallback(
    async (forceRetotal = false) => {
      try {
        //forcing a retotal here so auto applied discounts are applied, for some reason the useRemoteOrderTotal hook
        //keeps the previous order total even if it was cleared in app state.
        await loadRemoteOrderTotal(forceRetotal)
      } catch (e) {
        ToastManager.error(APPLICATION_STRINGS.PROMOTIONS_NOT_APPLIED)
        //continue processing if orderTotal fails and we can't apply promos
        return Promise.resolve()
      }
    },
    [loadRemoteOrderTotal],
  )

  const initiateTab = useCallback(
    async (tabName) => {
      if (!tabName) return

      startSpinner()

      if (getNetworkAvailableValue()) {
        await processPromotions(true)
      }

      //We need redirect to the current pathname once the tab is created in order to to clear
      //state in history which contains cardReaderData so it's not pulled in the next order
      const tabData = {
        tabName,
        redirectPath: history.location.pathname,
      }

      if (cfdMode === MODES.POS && !isEmpty(cfdAuthResponse.toJS())) {
        tabData.cardReaderData = cfdAuthResponse.toJS()
        tabData.paymentType = PAYMENT_TYPES.CREDIT_CARD
        // tabData.paymentId = order?.paymentId
        dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.UNSET, true))
      }

      if (cardReaderData && cfdMode !== MODES.POS) {
        tabData.cardReaderData = cardReaderData
        tabData.paymentType = history?.location?.state?.paymentType
        tabData.paymentId = history?.location?.state?.paymentId
      }

      dispatch({
        type: VNCFD_TRANSACTION_RESULT,
        data: {},
      })

      dispatch(startATab(tabData))
      setShowSelectTabPaymentMethod(false)

      if (!getNetworkAvailableValue() && cloudPrintingEnabled) {
        dispatch(setKitchenPrintErrorDialogOpen(true))
      }
    },
    [
      history.location.pathname,
      history.location?.state?.paymentType,
      history.location?.state?.paymentId,
      cfdMode,
      cfdAuthResponse,
      cardReaderData,
      dispatch,
      startSpinner,
      cloudPrintingEnabled,
      processPromotions,
    ],
  )

  const initiateTabAndCloseModal = useCallback(
    (tabName) => {
      startSpinner()
      setShowStartTabModal(false)
      dispatch(setIsTabNameRequired(false))
      initiateTab(tabName)
    },
    [dispatch, initiateTab, startSpinner],
  )

  useEffect(() => {
    return () => {
      dispatch(setStartATabModal({ open: false }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (showStartTabModal) return

    const shouldOpenModal =
      (cfdMode === MODES.POS &&
        !stepOneModal?.get('open') &&
        !stepTwoModal?.get('open') &&
        currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.CREDIT_CARD_AUTHORIZATION &&
        !isEmpty(cfdAuthResponse?.toJS())) ||
      (cfdMode !== MODES.POS && tabNameRequired)

    if (!shouldOpenModal) return

    stopSpinner()
    dispatch(setStartATabModal({ open: true, onSaveClick: initiateTabAndCloseModal, initialInputValue: getInitialInputValue }))
    tabNameInputRef?.current?.focus()
    setShowStartTabModal(true)

    if (cfdMode === MODES.POS) {
      dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.UNSET, true))
    }
  }, [
    cfdMode,
    currentPaymentFlow,
    cfdAuthResponse,
    stepOneModal,
    stepTwoModal,
    tabNameRequired,
    showStartTabModal,
    stopSpinner,
    dispatch,
    initiateTabAndCloseModal,
    getInitialInputValue,
  ])

  useEffect(() => {
    if (tabNameInputRef?.current) {
      tabNameInputRef?.current?.select()
    }
  }, [showStartTabModal])

  useEffect(() => {
    if (cfdMode !== MODES.POS) return
    if ((!success && !failed) || loading) return
    if (!showSelectTabPaymentMethod && !requirePaymentForTabs) return
    if (bridgePaymentMethods.executeAuthRequest !== bridgePaymentMethod) return

    const _calculatedCartValues = calculateCartTotals({
      remoteOrderTotal,
      cartItems: currentCart,
      taxRate,
      taxByItem,
      menuServiceCharge,
      orderInProgress: null,
      isFinalizingTip: null,
      tenderData: null,
      useNormalTaxRounding,
      useTaxClasses,
      taxClasses,
      taxRules,
      taxExempt,
    })
    const displayAmount = centsToDollars(_calculatedCartValues.total)

    dispatch(setCalculatedCurrentCartValues(_calculatedCartValues))

    let paymentData = {
      amount: displayAmount,
      tipAmount: 0,
      hRef: _calculatedCartValues.hRef,
      orderNumber: _calculatedCartValues.orderNumber,
      uuid: _calculatedCartValues.uuid,
      deviceSerial: getDeviceSerial(),
      ...getTransactionBrandingConfig(),
    }

    dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.CREDIT_CARD_AUTHORIZATION, true))
    dispatch(setCardAuthStepOne({ open: true, amount: displayAmount }))
    bridge_sendExecuteAuthRequest(paymentData)
    setShowSelectTabPaymentMethod(false)
  }, [
    bridgePaymentMethod,
    showSelectTabPaymentMethod,
    remoteOrderTotal,
    success,
    failed,
    loading,
    cfdMode,
    requirePaymentForTabs,
    currentCart,
    taxRate,
    taxByItem,
    menuServiceCharge,
    useNormalTaxRounding,
    useTaxClasses,
    taxClasses,
    taxRules,
    taxExempt,
    dispatch,
  ])

  const handleAuthorizePaymentRequest = useCallback(() => {
    dispatch(setStartATabModal({ open: false }))
    dispatch(setBridgePaymentMethod(bridgePaymentMethods.executeAuthRequest))

    if (cfdMode === MODES.POS) {
      startSpinner()
      loadRemoteOrderTotal(true)
      return
    }

    dispatch(setRoute('/card-reader', { redirectPath: '/concession-order', authTabs: true }))
  }, [dispatch, cfdMode, startSpinner, loadRemoteOrderTotal])

  const handleAddPaymentLater = useCallback(
    (_, tabName) => {
      dispatch(setTabPaymentMethodModal({ open: false }))
      dispatch(
        setStartATabModal({
          open: true,
          onSaveClick: (newTabName) => initiateTab(newTabName),
          initialInputValue: tabName,
        }),
      )
    },
    [dispatch, initiateTab],
  )

  const handleStartTab = useCallback(() => {
    if (requirePaymentForTabs) {
      handleAuthorizePaymentRequest()
      return
    }
    setShowSelectTabPaymentMethod(true)

    dispatch(
      setTabPaymentMethodModal({
        open: true,
        handleAddPaymentLater,
        handleAuthorizePaymentRequest,
        shouldShowAddPaymentLater: !requirePaymentForTabs,
        authNewText: true,
      }),
    )
  }, [requirePaymentForTabs, dispatch, handleAddPaymentLater, handleAuthorizePaymentRequest])

  /**
   * addToTab logic:
   *
   *          order in Stadium   | order not in Stadium
   *         +-------------------+---------------------+
   * online  | call mutation api | add items locally   |
   *         +-------------------+---------------------+
   * offline | can't add items   | add items locally   |
   *         +-------------------+---------------------+
   *
   */
  const addToTabHandler = useCallback(async () => {
    const networkConnected = getNetworkAvailableValue()

    startSpinner(APPLICATION_STRINGS.PROCESSING)

    // Online processing
    if (networkConnected) {
      // Order aready in Stadium then we can mutate the order
      if (orderInProgress.wasCreatedInStadium) {
        // should adding items fail, redirect to orders
        const failureCallback = () => {
          dispatch(setRoute('/orders'))
        }

        dispatch(addItemsToTab({ failureCallback }))
        return
      }
      // Order is not in Stadium so even we're online we can't mutate we can just do a local update
      dispatch(addItemsToTabOffline())
      return
    }

    // offline and order already in stadium then we need to error off
    if (orderInProgress.wasCreatedInStadium) {
      ToastManager.error("Items can't be added offline for this order")
      stopSpinner()
      return
    }

    if (cloudPrintingEnabled) {
      dispatch(setKitchenPrintErrorDialogOpen(true))
    }

    // offline and order is not in stadium
    dispatch(addItemsToTabOffline())
  }, [dispatch, orderInProgress, cloudPrintingEnabled, startSpinner, stopSpinner])

  if (!tabsModeEnabled) {
    return null
  }
  if (isTabbed(orderInProgress)) {
    return (
      <div className={styles.payments}>
        <Button
          useMui
          className={cn([styles.checkout, styles.long])}
          disabled={!hasCartItems || mutationInProgress}
          onClick={addToTabHandler}
        >
          {APPLICATION_STRINGS.ADD_TO_TAB}
        </Button>
      </div>
    )
  }

  return (
    <div className={styles.payments}>
      <PayNowButton className={cn([styles.checkout, styles.long])} />
      <Button
        useMui
        className={cn([styles.checkout, styles.long])}
        disabled={!hasCartItems || mutationInProgress}
        onClick={handleStartTab}
      >
        {APPLICATION_STRINGS.START_A_TAB}
      </Button>
    </div>
  )
}

export default memo(VNTabButtons)
